import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const CELL_SIZE = 11;
const CELL_GAP = 2;
const CELL_STEP = CELL_SIZE + CELL_GAP;

/** Devuelve colores según la puntuación: escala naranja → verde, sobre 15 puntos posibles. */
function getActivityColor(score) {
  if (score === null || score === undefined) return { bg: '#f0f0f0', border: '#e0e0e0' };
  if (score === 0)    return { bg: '#bdbdbd', border: '#9e9e9e' };
  if (score <= 3)     return { bg: '#FF8C00', border: '#e07b00' }; // naranja
  if (score <= 6)     return { bg: '#FFC107', border: '#e0a800' }; // naranja-amarillo
  if (score <= 9)     return { bg: '#FFD740', border: '#e0bc00' }; // amarillo
  if (score <= 12)    return { bg: '#8BC34A', border: '#6a9a2e' }; // verde clarito
  if (score < 15)     return { bg: '#4CAF50', border: '#388e3c' }; // verde fuerte
  return                 { bg: '#4CAF50', border: '#FFD700', perfect: true }; // 15: verde fuerte + borde dorado
}

/** Devuelve si el año es bisiesto. */
function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Número de días del año. */
function daysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

/** Día de la semana (0=Lun, 6=Dom) para una fecha. */
function dayOfWeek(date) {
  const d = date.getDay(); // 0=Dom, 1=Lun...6=Sáb
  return (d + 6) % 7;     // 0=Lun, ..., 6=Dom
}

const DAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MONTH_NAMES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export default function ActivityCalendar({ data = [], year, onYearChange }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // Mapa fecha (ISO string) -> score
  const scoreMap = useMemo(() => {
    const m = {};
    data.forEach((d) => {
      m[d.date] = d.score;
    });
    return m;
  }, [data]);

  // Construir la cuadrícula: semanas × 7 días
  const { weeks, monthLabels } = useMemo(() => {
    const jan1 = new Date(year, 0, 1);
    const startOffset = dayOfWeek(jan1); // cuántos días vacíos al inicio
    const totalDays = daysInYear(year);

    // Todas las celdas: null (vacía) o { date: 'YYYY-MM-DD', score }
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 0; d < totalDays; d++) {
      const date = new Date(year, 0, d + 1);
      const iso = date.toISOString().slice(0, 10);
      cells.push({ date: iso, score: scoreMap[iso] ?? null });
    }
    // Rellenar hasta múltiplo de 7
    while (cells.length % 7 !== 0) cells.push(null);

    // Agrupar en semanas (columnas)
    const weekCount = cells.length / 7;
    const weeksArr = [];
    for (let w = 0; w < weekCount; w++) {
      weeksArr.push(cells.slice(w * 7, w * 7 + 7));
    }

    // Etiquetas de mes: posición (columna) donde empieza cada mes
    const labelsArr = [];
    let lastMonth = -1;
    for (let w = 0; w < weeksArr.length; w++) {
      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const cell = weeksArr[w][dayIdx];
        if (cell) {
          const month = new Date(cell.date + 'T00:00:00').getMonth();
          if (month !== lastMonth) {
            labelsArr.push({ col: w, month });
            lastMonth = month;
          }
        }
      }
    }

    return { weeks: weeksArr, monthLabels: labelsArr };
  }, [year, scoreMap]);

  const gridWidth = weeks.length * CELL_STEP - CELL_GAP;
  const LABEL_COL_WIDTH = 16;

  return (
    <Box>
      {/* Cabecera: título + navegación de año */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton size="small" onClick={() => onYearChange(year - 1)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {year}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onYearChange(year + 1)}
          disabled={year >= new Date().getFullYear()}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scroll horizontal en móvil */}
      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0 }}>

          {/* Etiquetas de mes */}
          <Box sx={{ display: 'flex', ml: `${LABEL_COL_WIDTH}px`, mb: '2px', position: 'relative', height: 14 }}>
            {monthLabels.map(({ col, month }) => (
              <Typography
                key={`${col}-${month}`}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  left: col * CELL_STEP,
                  fontSize: '0.6rem',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {MONTH_NAMES_ES[month]}
              </Typography>
            ))}
          </Box>

          {/* Grid de días */}
          <Box sx={{ display: 'flex', gap: `${CELL_GAP}px` }}>
            {/* Etiquetas de día de la semana */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${CELL_GAP}px`, width: LABEL_COL_WIDTH }}>
              {DAY_LABELS.map((label, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.55rem', height: CELL_SIZE, lineHeight: `${CELL_SIZE}px`, textAlign: 'right', pr: '3px' }}
                >
                  {i % 2 === 0 ? label : ''}
                </Typography>
              ))}
            </Box>

            {/* Columnas semanales */}
            {weeks.map((week, wIdx) => (
              <Box key={wIdx} sx={{ display: 'flex', flexDirection: 'column', gap: `${CELL_GAP}px` }}>
                {week.map((cell, dIdx) => {
                  if (!cell) {
                    return (
                      <Box
                        key={dIdx}
                        sx={{ width: CELL_SIZE, height: CELL_SIZE, flexShrink: 0 }}
                      />
                    );
                  }
                  const { bg, border, perfect } = getActivityColor(cell.score);
                  const dateLabel = new Date(cell.date + 'T00:00:00').toLocaleDateString(
                    i18n.language,
                    { day: 'numeric', month: 'short', year: 'numeric' }
                  );
                  const scoreLabel = cell.score === null
                    ? 'Sin jugar'
                    : cell.score === 0
                    ? '0 pts'
                    : `${cell.score} pts`;

                  return (
                    <Tooltip
                      key={dIdx}
                      title={`${dateLabel} · ${scoreLabel}`}
                      arrow
                      placement="top"
                    >
                      <Box
                        onClick={() => navigate(`/history?date=${cell.date}`)}
                        sx={{
                          width: CELL_SIZE,
                          height: CELL_SIZE,
                          borderRadius: '2px',
                          bgcolor: bg,
                          border: `${perfect ? 2 : 1}px solid ${border}`,
                          boxShadow: perfect ? `0 0 3px ${border}` : 'none',
                          flexShrink: 0,
                          cursor: 'pointer',
                          transition: 'transform 0.1s, opacity 0.1s',
                          '&:hover': { opacity: 0.75, transform: 'scale(1.3)' },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Leyenda */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
        {[
          { score: null,  label: 'Sin jugar' },
          { score: 0,     label: '0 pts' },
          { score: 2,     label: '1-3 pts' },
          { score: 5,     label: '4-6 pts' },
          { score: 8,     label: '7-9 pts' },
          { score: 11,    label: '10-12 pts' },
          { score: 14,    label: '13-14 pts' },
          { score: 15,    label: '¡15! Perfecto' },
        ].map(({ score, label }) => {
          const { bg, border, perfect } = getActivityColor(score);
          return (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  borderRadius: '2px',
                  bgcolor: bg,
                  border: `${perfect ? 2 : 1}px solid ${border}`,
                  boxShadow: perfect ? `0 0 3px ${border}` : 'none',
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
